import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Building2, Users, Briefcase, ArrowRight, Sparkles, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Layout } from '@/components/wiki/Layout';
import { useSEO } from '@/hooks/useSEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HomePage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [recentPages, setRecentPages] = useState([]);
  
  // SEO meta tags
  useSEO({
    title: null, // Use default title for homepage
    description: 'Handshake is the comprehensive wiki encyclopedia for business. Research Fortune 500 companies, executives, investors, startups, and venture funding. Free access to 500+ company profiles.',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecentPages = async () => {
      try {
        const response = await axios.get(`${API}/pages?limit=6`);
        setRecentPages(response.data);
      } catch (error) {
        console.error('Error fetching pages:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRecentPages();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-[#f8f9fa] border-b border-[#a2a9b1] overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
          <div className="text-center">
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 tracking-tight text-[#202122]">
              Handshake
            </h1>
            <p className="text-base sm:text-lg text-[#54595d] mb-8 max-w-3xl mx-auto">
              The free encyclopedia for business. Learn about companies, executives, investors, and venture capital.
            </p>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#54595d]" />
                <Input
                  type="search"
                  placeholder="Search Handshake..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-4 py-5 text-base rounded-sm bg-white text-[#202122] border-[#a2a9b1] placeholder:text-[#72777d] focus:border-[#36c] focus:ring-[#36c]"
                  data-testid="hero-search-input"
                />
                <Button 
                  type="submit"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-sm bg-[#36c] hover:bg-[#2a4b8d] text-white px-6 font-medium"
                  data-testid="hero-search-btn"
                >
                  Search
                </Button>
              </div>
            </form>

            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/create">
                <Button size="lg" className="bg-[#36c] text-white hover:bg-[#2a4b8d] font-medium px-6" data-testid="hero-create-btn">
                  <Sparkles className="mr-2 h-4 w-4" />
                  Create a Page
                </Button>
              </Link>
              <Link to="/search">
                <Button size="lg" variant="outline" className="border-[#a2a9b1] text-[#36c] hover:bg-[#eaecf0] px-6" data-testid="hero-browse-btn">
                  Browse All Pages
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-12 bg-white border-b border-[#c8ccd1]" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-xl font-semibold text-center mb-8 text-[#202122]">
            Browse by Category
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Building2, label: 'Businesses', category: 'Businesses' },
              { icon: Users, label: 'People', category: 'People' },
              { icon: Briefcase, label: 'Investors', category: 'Investors' },
              { icon: Package, label: 'Products', category: 'Products' },
            ].map((cat) => (
              <Link
                key={cat.label}
                to={`/search?category=${cat.category}`}
                className="group bg-[#f8f9fa] hover:bg-[#eaecf0] rounded-sm p-6 text-center transition-all duration-200 border border-[#c8ccd1] hover:border-[#36c]"
                data-testid={`category-${cat.label.toLowerCase()}`}
              >
                <cat.icon className="h-8 w-8 mx-auto mb-3 text-[#36c] transition-colors" />
                <span className="font-medium text-[#202122] text-sm">{cat.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Pages */}
      <section className="py-12 bg-[#f8f9fa]" data-testid="recent-pages-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-serif text-xl font-semibold text-[#202122]">
              Recent Pages
            </h2>
            <Link to="/search" className="text-[#36c] hover:underline flex items-center gap-1 text-sm">
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-sm p-5 animate-pulse border border-[#c8ccd1]">
                  <div className="h-5 bg-[#eaecf0] rounded w-3/4 mb-3" />
                  <div className="h-4 bg-[#eaecf0] rounded w-full mb-2" />
                  <div className="h-4 bg-[#eaecf0] rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : recentPages.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentPages.map((page) => (
                <Link
                  key={page.id}
                  to={`/wiki/${page.slug}`}
                  className="bg-white rounded-sm p-5 hover:border-[#36c] transition-all duration-200 border border-[#c8ccd1] group"
                  data-testid={`page-card-${page.slug}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium text-[#36c] group-hover:underline">
                      {page.title}
                    </h3>
                    {page.is_sponsored && (
                      <span className="text-xs px-2 py-0.5 bg-[#fee7e6] text-[#d33] rounded">
                        Sponsored
                      </span>
                    )}
                  </div>
                  {page.summary && (
                    <p className="text-[#54595d] text-sm line-clamp-2">
                      {page.summary}
                    </p>
                  )}
                </Link>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-sm p-12 text-center border border-[#c8ccd1]">
              <Building2 className="h-12 w-12 mx-auto mb-4 text-[#72777d]" />
              <h3 className="font-medium text-[#202122] mb-2">No pages yet</h3>
              <p className="text-[#54595d] mb-6 text-sm">Be the first to create a page on Handshake</p>
              <Link to="/create">
                <Button className="bg-[#36c] hover:bg-[#2a4b8d] text-white font-medium" data-testid="empty-create-btn">
                  Create the First Page
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-white border-t border-b border-[#c8ccd1]" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-2xl font-semibold mb-3 text-[#202122]">
            Claim Your Page
          </h2>
          <p className="text-[#54595d] mb-6 max-w-xl mx-auto text-sm">
            Take control of your profile. Add official contact information, social links, and protect your page from unauthorized edits.
          </p>
          <Link to="/pricing">
            <Button size="lg" className="bg-[#36c] hover:bg-[#2a4b8d] text-white font-medium px-6" data-testid="cta-claim-btn">
              <Sparkles className="mr-2 h-4 w-4" />
              Starting at $69/month
            </Button>
          </Link>
        </div>
      </section>

      {/* About Us Section */}
      <section className="py-12 bg-[#f8f9fa]" data-testid="about-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl font-semibold mb-6 text-[#202122] text-center">
            About Handshake
          </h2>
          
          <div className="flex justify-center gap-8 mb-8">
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-white border border-[#c8ccd1] flex items-center justify-center">
                <span className="text-xl">📚</span>
              </div>
              <h3 className="font-medium text-[#202122] text-sm mb-1">Wikipedia</h3>
              <p className="text-[#54595d] text-xs">For academics</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-white border border-[#c8ccd1] flex items-center justify-center">
                <span className="text-xl">🎬</span>
              </div>
              <h3 className="font-medium text-[#202122] text-sm mb-1">Fandom</h3>
              <p className="text-[#54595d] text-xs">For fans</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-[#eaf3ff] border border-[#36c] flex items-center justify-center">
                <span className="text-xl">🤝</span>
              </div>
              <h3 className="font-medium text-[#36c] text-sm mb-1">Handshake</h3>
              <p className="text-[#54595d] text-xs">For business</p>
            </div>
          </div>

          <div className="bg-white rounded-sm p-6 border border-[#c8ccd1] space-y-4 text-sm">
            <p className="text-[#202122] leading-relaxed">
              Handshake is a wiki site focused on business topics, like 
              <span className="text-[#36c]"> financial performance</span>, 
              <span className="text-[#36c]"> business partnerships</span>, 
              <span className="text-[#36c]"> corporate culture</span>, 
              <span className="text-[#36c]"> investments</span>, 
              <span className="text-[#36c]"> venture capital</span>, 
              <span className="text-[#36c]"> startups</span>, 
              <span className="text-[#36c]"> hiring</span>, and 
              <span className="text-[#36c]"> marketing</span>. 
              We offer a place for in-depth wiki profiles about businesses, people, and other topics.
            </p>
            <p className="text-[#202122] leading-relaxed">
              Have an interview coming up with the CEO? If they have a profile here, you can learn how the CEO pivoted the business and shifted its priorities. Want to invest in a startup? Learn about their past valuations, amount of funding, financial performance, and history.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
